import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { ThreeDotsLoading } from "../loading";
import CommonSwitch from "../switch";
import theme from "../../theme";
import TransactionHistory from "./transactionHistory";
import DownloadStatementModal from "./downloadStatementModal";
import { getEmployeeInfoAction, promoteHrToAdminAction } from "../../containers/dashboard/action";
import { hasCap, isSuper } from "../../lib/permissions";
import {
  changeEmployeePermissionAction,
  changeEmployeePermissionSuccessAction,
  changeEmployeePermissionErrorAction,
} from "../../containers/companyDashboard/action";
import ConfirmSwitchModal from "../confirmSwitchModal";
import { FormControlLabel, Modal } from "@material-ui/core";
import AlertDialog from "../dialog/alert";
import CommonButton from "../button";
import { moneyValue } from "../../utils";
import moment from "moment";
import { getStartEndDate } from "../../utils";

function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}


const useStyles = makeStyles(() => ({
  container: {
    width: "350px",
    backgroundColor: theme.colors.White,
    boxShadow: "0px 0px 10px #CFD9EA80",
    borderRadius: "15px",
    padding: "50px 60px",
  },
  title: {
    color: theme.colors.DodgerBlue,
    fontSize: "24px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    marginBottom: "20px",
  },
  subText: {
    color: theme.colors.Violet,
    fontSize: "18px",
    fontFamily: theme.fontFamily.MARKPRO,
    marginBottom: "10px",
  },
  fileContainer: {
    backgroundColor: theme.colors.Zumthor,
    padding: "5px 10px",
    top: "15px",
    position: "relative",
    borderRadius: "3px",
    wordBreak: "break-word",
  },
  fileName: {
    color: theme.colors.DodgerBlue,
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
  },
  crossIcon: { marginLeft: "8px", marginBottom: "-4px", cursor: "pointer" },
  bingoImage: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "40px",
  },
  modalPaper: {
    position: "absolute",
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    position: 'absolute',
    width: 400,
    backgroundColor: '#fff',
    border: '2px solid #000',
    boxShadow: '5px 5px 15px rgba(0, 0, 0, 0.5)',
    padding: 20,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  infoIco: {
    color: theme.colors.DodgerBlueLight,
    // backgroundColor: theme.colors.DodgerBlueLight
  }
  ,
  detailContainer: {
    marginTop: "30px",
    "& .block": {
      cursor: "pointer",
      background: theme.colors.White,
      boxShadow: "0px 2px 15px #D0CFDB99",
      borderRadius: "15px",
      padding: "30px",
      "& .header": {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        "& .employeeName": {
          fontFamily: theme.fontFamily.MARKPROBOLD,
          fontSize: "22px",
        },
        "& .editContainer": {
          display: "flex",
          alignItems: "center",
          "& img": { height: "24px", width: "24px" },
          "& .editText": {
            fontFamily: theme.fontFamily.MARKPRO,
            fontSize: "12px",
            color: theme.colors.DodgerBlue,
            textDecoration: "underline",
            cursor: "pointer",
          },
        },
      },
      "& .information": {
        display: "flex",
        // alignItems: "center",
        "& .left": { flex: "0.5" },
        "& .right": {
          flex: "0.5",
          "& .title": {
            fontSize: "13px",
            fontFamily: theme.fontFamily.MARKPROHEAVY,
            opacity: 0.3,
            padding: "10px 0px",
          },
        },
        "& .flexContainer": {
          display: "flex",
          alignItems: "center",
          fontSize: "12px",
          "& .label": { flex: "0.5", fontFamily: theme.fontFamily.MARKPRO },
          "& .link": { flex: "0.5", fontFamily: theme.fontFamily.MARKPRO, transition: ".3s ease-in-out" },
          "& .link:hover": { textDecoration: "underline" },
          "& .val": {
            flex: "0.5",
            fontFamily: theme.fontFamily.MARKPROBOLD,
            "& .currency": {
              fontSize: "8px",
              position: "relative",
              bottom: "3px",
              marginRight: "2px",
            },
          },
          "& .changeLink": {
            color: theme.colors.DodgerBlue,
            marginLeft: "5px",
            display: "none",
          },
        },
      },
    },
  },
}));

function EmployeeDetail({
  setEditEmployeeDetail,
  employeeDetail,
  updateEmployeeMethod,
  createPayStackProfileActionMethod,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const modalStyle = getModalStyle()

  
  let _storedUser = null;
  try {
    _storedUser = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    _storedUser = null;
  }
  const isCompanyManager = !!_storedUser?.is_admin || !!_storedUser?.is_hr;
  const canEditEmployees =
    hasCap(_storedUser, "employees.edit") || isCompanyManager;
  const canPromote = isSuper(_storedUser);

  const canViewTransactions =
    hasCap(_storedUser, "transactions.view") || isCompanyManager;

  // Backoffice-only: download an employee's payment statement PDF. Super or
  // payments.view (hasCap covers both); hidden entirely otherwise.
  const onBackoffice =
    typeof window !== "undefined" &&
    window.location.pathname.includes("backoffice");
  const canDownloadStatement = onBackoffice && hasCap(_storedUser, "payments.view");
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const getEmployeeInfoMethod = useCallback(
    (data) => dispatch(getEmployeeInfoAction(data)),
    [dispatch]
  );
  const promoteHrToAdminActionMethod = useCallback(
    (data) => dispatch(promoteHrToAdminAction(data)),
    [dispatch]
  );

  const dashboardReducer = useSelector((state) => {
    return state.dashboardReducer;
  });

  const loginReducer = useSelector((state) => {
    return state.loginReducer;
  });

  const companyDashboardReducer = useSelector((state) => {
    return state.companyDashboardReducer;
  });

  const [confirmSwitch, setConfirmSwitch] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [switchRecord, setSwitchRecord] = useState(null);
  const [switchMethod, setSwitchMethod] = useState(null);
  const [showHrToAdminConfirm, setShowHrToAdminConfirm] = useState({ "hr": null, show: false });
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);

  // Permission (ordinary / hr / admin) state — `pending` holds the target
  // shape { is_admin, is_hr } while the confirm dialog is open. Only one of
  // is_admin / is_hr may be true; both false = ordinary.
  const [pendingPermission, setPendingPermission] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const currentUser = (() => {
    try { return JSON.parse(localStorage.user || "{}"); } catch (e) { return {}; }
  })();
  const canManagePermissions =
    !!currentUser?.is_admin &&
    !!dashboardReducer?.employeeInfo?.id &&
    currentUser?.id !== dashboardReducer?.employeeInfo?.id;

  const currentIsAdmin = !!dashboardReducer?.employeeInfo?.is_admin;
  const currentIsHr = !!dashboardReducer?.employeeInfo?.is_hr;

  // Notistack + refetch when the mutation lands. Handled here (not in the
  // saga) so the toast copy can reference the actual employee name.
  useEffect(() => {
    if (companyDashboardReducer?.changeEmployeePermissionSuccess) {
      enqueueSnackbar("Permission updated.", { variant: "success" });
      dispatch(changeEmployeePermissionSuccessAction(null));
      if (employeeDetail?.id) getEmployeeInfoMethod(employeeDetail.id);
      setPendingPermission(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDashboardReducer?.changeEmployeePermissionSuccess]);

  useEffect(() => {
    const err = companyDashboardReducer?.changeEmployeePermissionError;
    if (!err) return;
    const messages = Array.isArray(err) ? err : [err];
    messages.filter(Boolean).forEach((m) =>
      enqueueSnackbar(typeof m === "string" ? m : "Failed to update permission.", { variant: "error" })
    );
    dispatch(changeEmployeePermissionErrorAction(null));
    setPendingPermission(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDashboardReducer?.changeEmployeePermissionError]);

  // A checkbox toggle picks one of three end-states. Radio-like semantics on
  // the wire ({T,F}, {F,T}, {F,F}), enforced client-side so we never send
  // {T,T}. Server double-checks.
  const permissionLabel = ({ is_admin, is_hr }) =>
    is_admin ? "Admin" : is_hr ? "HR" : "ordinary";

  const requestPermissionChange = (nextState) => {
    // Guard: no-op if it's already the current state
    if (!!nextState.is_admin === currentIsAdmin && !!nextState.is_hr === currentIsHr) return;
    setPendingPermission(nextState);
  };

  const confirmPermissionChange = () => {
    if (!pendingPermission || !dashboardReducer?.employeeInfo?.id) return;
    dispatch(
      changeEmployeePermissionAction(dashboardReducer.employeeInfo.id, {
        is_admin: !!pendingPermission.is_admin,
        is_hr: !!pendingPermission.is_hr,
      })
    );
  };

  useEffect(() => {
    if (
      confirmation === "yes" &&
      switchRecord &&
      switchRecord.id &&
      switchMethod
    ) {
      if (switchMethod === "updateEmployeeMethod")
        updateEmployeeMethod(
          {
            is_active: !switchRecord.is_active,
          },
          switchRecord.id
        );
      setSwitchRecord(null);
      setSwitchMethod(null);
      setConfirmation(null);
    }
  }, [confirmation]);

  useEffect(() => {
    if (companyDashboardReducer?.updateEmployeeSuccess && employeeDetail?.id) {
      getEmployeeInfoMethod(employeeDetail.id);
    }
  }, [
    companyDashboardReducer,
    setEditEmployeeDetail,
    employeeDetail,
    getEmployeeInfoMethod,
  ]);
  useEffect(() => {
    if ((dashboardReducer?.promoteHRToAdmin)?.hr && (dashboardReducer?.promoteHRToAdmin?.hr === dashboardReducer?.employeeInfo?.id)) {
      setShowHrToAdminConfirm({ hr: null, show: false });
      getEmployeeInfoMethod(dashboardReducer?.promoteHRToAdmin?.hr);
      setOpenSuccessDialog(true)
    }
  }, [dashboardReducer?.promoteHRToAdmin, getEmployeeInfoMethod])
  useEffect(() => {
    if (employeeDetail?.id) {
      getEmployeeInfoMethod(employeeDetail.id);
    }
  }, [employeeDetail, getEmployeeInfoMethod]);
  const companyInfoL = getPriorityDataForSignedInUser(dashboardReducer?.companyInfo, loginReducer?.companyInformation);
  const [companyStartDate, companyEndDate] = getStartEndDate(new Date(), Number.parseInt(companyInfoL?.start_month) || 1, Number.parseInt(companyInfoL?.end_month) || 31);
  
  return dashboardReducer?.employeeInfo ? (
    <div className={classes.detailContainer}>
      <ThreeDotsLoading
        showLoader={dashboardReducer?.loading}
      />
      {dashboardReducer?.error?.length ||
        companyDashboardReducer?.error?.length ? (
        <div className={"error-container"}>
          {dashboardReducer?.error?.length
            ? dashboardReducer.error.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : null}

          {companyDashboardReducer?.error?.length
            ? companyDashboardReducer.error.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : null}
        </div>
      ) : ""}

      <div className="block">
        <div className="header">
          <div className="employeeName">
            {dashboardReducer?.employeeInfo?.first_name}{" "}
            {dashboardReducer?.employeeInfo?.last_name}
          </div>
          <div className="editContainer">
            {canEditEmployees ? (
            <div className="editContainer">
              <div
                className="editContainer" style={{ marginRight: "10px" }}
                onClick={() => {
                  createPayStackProfileActionMethod({
                    employee_id: employeeDetail.id,
                    company_id: dashboardReducer?.companyInfo?.id
                  });
                }}
              >
                <img src="/images/add.svg" style={{ height: "15px" }} alt="editIcon" />
                <span className="editText">Create PayStack profile</span>
              </div>
            </div>
            ) : null}
            {setEditEmployeeDetail && canEditEmployees ? (
              <div
                className="editContainer"
                onClick={() => {
                  setEditEmployeeDetail(dashboardReducer.employeeInfo);
                }}
              >
                <img src="/images/editIcon.png" alt="editIcon" />
                <span className="editText">Edit</span>
              </div>
            ) : null}
            {canDownloadStatement ? (
              <div
                className="editContainer"
                style={{ marginLeft: "10px" }}
                onClick={() => setShowDownloadModal(true)}
              >
                <img
                  src="/images/download.png"
                  style={{ height: "15px" }}
                  alt="download"
                />
                <span className="editText">Download PDF</span>
              </div>
            ) : null}
          </div>
        </div>
        <div className="information">
          <div className="left">
            <div className="flexContainer">
              <div className="label">Employee</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.first_name}{" "}
                {dashboardReducer?.employeeInfo?.last_name}
                <a href="#" className="changeLink">
                  Change
                </a>
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Employee ID</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.employee_id || "NULL"}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Email</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.email}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Phone Number</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.profile?.phone}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Gender</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.gender
                  ? dashboardReducer.employeeInfo.gender === "F"
                    ? "Female"
                    : dashboardReducer.employeeInfo.gender === "M"
                      ? "Male"
                      : ""
                  : ""}
              </div>
            </div>
            {
              (
                (
                  dashboardReducer?.employeeInfo?.country && dashboardReducer?.employeeInfo?.country === "NG"
                ) || JSON.parse(localStorage.getItem("user"))?.country === "NG"
              ) ? (
                <><div className="flexContainer">
                  <div className="label">NIN</div>
                  <div className="val" style={{ padding: "10px 0px" }}>
                    {dashboardReducer?.employeeInfo?.profile?.nin?.length
                      ? dashboardReducer.employeeInfo.profile?.nin
                      : null}
                  </div>
                </div>
                  <div className="flexContainer">
                    <div className="label">BVN</div>
                    <div className="val" style={{ padding: "10px 0px" }}>
                      {dashboardReducer?.employeeInfo?.profile?.bvn?.length
                        ? dashboardReducer.employeeInfo.profile?.bvn
                        : null}
                    </div>
                  </div></>
              ) : null
            }
            <div className="flexContainer">
              <div className="label">Account</div>
              <div className="val">
                <span style={{ fontSize: "12px" }}>Activated</span>
                <CommonSwitch
                  name={dashboardReducer?.employeeInfo?.id}
                  checked={dashboardReducer?.employeeInfo?.is_active}
                  disabled={!updateEmployeeMethod}
                  onClick={(e) => {
                    if (
                      dashboardReducer?.employeeInfo?.id &&
                      updateEmployeeMethod
                    ) {
                      e.stopPropagation();
                      setConfirmSwitch(true);
                      setSwitchRecord(dashboardReducer.employeeInfo);
                      setSwitchMethod("updateEmployeeMethod");
                    }
                  }}
                />
              </div>
            </div>
            {" "}
            <div className="flexContainer">
              <div className="label">Job Title</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.profile?.position}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Monthly Pay</div>
              {
                dashboardReducer?.employeeInfo?.profile?.upcoming_salary ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    <div className="val" style={{ padding: "10px 0px" }}>
                      <span className={"currency"}>
                        {dashboardReducer?.companyInfo?.currency ||
                          loginReducer?.companyInformation?.currency}
                      </span>{" "}
                      {moneyValue(dashboardReducer?.employeeInfo?.profile?.upcoming_salary)}
                    </div>
                    <small>
                      Their salary would be updated from {moneyValue(dashboardReducer?.employeeInfo?.profile?.salary)} to {moneyValue(dashboardReducer?.employeeInfo?.profile?.upcoming_salary)}
                      <br /> {moment(companyEndDate).format("[at the end of month cycle on ] DD MMM YYYY")}</small>

                  </div>

                ) : (<div className="val" style={{ padding: "10px 0px" }}>
                  <span className={"currency"}>
                    {dashboardReducer?.companyInfo?.currency ||
                      loginReducer?.companyInformation?.currency}
                  </span>{" "}
                  {moneyValue(dashboardReducer?.employeeInfo?.profile?.salary)}
                </div>)
              }

            </div>
            <div className="flexContainer">
              <div className="label">Current Balance On Limit</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                <span className={"currency"}>
                  {dashboardReducer?.companyInfo?.currency ||
                    loginReducer?.companyInformation?.currency}
                </span>{" "}
                {moneyValue(dashboardReducer?.employeeInfo?.current_balance)}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Salary Withdrawal Limit</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                <span className={"currency"}>
                  {dashboardReducer?.companyInfo?.currency ||
                    loginReducer?.companyInformation?.currency}
                </span>{" "}
                {moneyValue(dashboardReducer?.employeeInfo?.withdrawal_limit)}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Savings</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                <span className={"currency"}>
                  {dashboardReducer?.companyInfo?.currency ||
                    loginReducer?.companyInformation?.currency}
                </span>{" "}
                {moneyValue(dashboardReducer?.employeeInfo?.profile?.savings)}
              </div>
            </div>
            {" "}
            {/* <div className="flexContainer">
              <div className="label">Leaves</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                <span className={"currency"}>
                  {dashboardReducer?.companyInfo?.currency ||
                    loginReducer?.companyInformation?.currency}
                </span>{" "}
                {dashboardReducer?.employeeInfo?.profile?.leaves
                  ? Math.round(
                      dashboardReducer?.employeeInfo?.profile?.leaves * 1000
                    ) / 1000
                  : 0}
              </div>
            </div> */}
            {/* {dashboardReducer?.employeeInfo?.deductions?.length
              ? dashboardReducer.employeeInfo.deductions.map((each) => (
                  <div className="flexContainer" key={each.id}>
                    <div className="label">{each.name}</div>
                    <div className="val" style={{ padding: "10px 0px" }}>
                      <span className={"currency"}>
                        {dashboardReducer?.companyInfo?.currency ||
                          loginReducer?.companyInformation?.currency}
                      </span>{" "}
                      {each.amount ? Math.round(each.amount * 1000) / 1000 : 0}
                    </div>
                  </div>
                ))
              : null} */}
          </div>
          <div className="right">
            <div className="title">Bank Information</div>
            <div className="flexContainer">
              <div className="label">Account Holder Name</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.banks?.length
                  ? dashboardReducer.employeeInfo.banks[0].account_holder
                  : null}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Bank Name</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.banks?.length
                  ? dashboardReducer.employeeInfo.banks[0].name
                  : null}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Bank Code</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.banks?.length
                  ? dashboardReducer.employeeInfo.banks[0].code
                  : null}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Paystack ID</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.banks?.length
                  ? dashboardReducer.employeeInfo.banks[0].paystack_id
                  : null}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Recipient Code</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.banks?.length
                  ? dashboardReducer.employeeInfo.banks[0].recipient_code
                  : null}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">Account Number</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.banks?.length
                  ? dashboardReducer.employeeInfo.banks[0].number
                  : null}
              </div>
            </div>
            <div className="flexContainer">
              <div className="label">IBAN</div>
              <div className="val" style={{ padding: "10px 0px" }}>
                {dashboardReducer?.employeeInfo?.banks?.length
                  ? dashboardReducer.employeeInfo.banks[0].iban
                  : null}
              </div>
            </div>
            {dashboardReducer?.employeeInfo?.branch && <>
              <div className="title">Office Location</div>
              <div className="flexContainer">
                <div className="label">Name</div>
                <div className="val" style={{ padding: "10px 0px" }}>
                  {dashboardReducer.employeeInfo.branch}
                </div>
              </div>
            </>}

            {(
              JSON.parse(localStorage.user)?.is_super && dashboardReducer?.employeeInfo?.is_hr
            ) && <>
                <div className="title">Upgrades</div>
                <div className="flexContainer">
                  <div className="link" onClick={() => {
                    setShowHrToAdminConfirm({ "hr": dashboardReducer?.employeeInfo?.id, show: true })
                  }} style={{ maxWidth: "200px" }}>
                    Promote to Hr
                  </div>
                </div>
              </>}

            {canManagePermissions && (
              <>
                <div className="title">Permissions</div>
                <div
                  className="flexContainer"
                  style={{ flexDirection: "column", alignItems: "flex-start", gap: "6px", padding: "6px 0" }}
                >
                  <FormControlLabel
                    control={
                      <CommonSwitch
                        checked={currentIsAdmin}
                        disabled={!!companyDashboardReducer?.changeEmployeePermissionLoading}
                        onChange={(e) =>
                          requestPermissionChange({
                            // Turning Admin on → HR must be off. Turning Admin
                            // off → ordinary (HR was already false by mutual
                            // exclusion, so no need to preserve).
                            is_admin: e.target.checked,
                            is_hr: e.target.checked ? false : false,
                          })
                        }
                      />
                    }
                    label={<span style={{ fontSize: 13, fontFamily: theme.fontFamily.MARKPROMEDIUM, color: theme.colors.Violet }}>Admin</span>}
                  />
                  <FormControlLabel
                    control={
                      <CommonSwitch
                        checked={currentIsHr}
                        disabled={!!companyDashboardReducer?.changeEmployeePermissionLoading}
                        onChange={(e) =>
                          requestPermissionChange({
                            is_admin: e.target.checked ? false : false,
                            is_hr: e.target.checked,
                          })
                        }
                      />
                    }
                    label={<span style={{ fontSize: 13, fontFamily: theme.fontFamily.MARKPROMEDIUM, color: theme.colors.Violet }}>HR</span>}
                  />
                  <div style={{ fontSize: 11, color: "#8892a4", fontFamily: theme.fontFamily.MARKPRO, marginTop: 2 }}>
                    Pick one — or leave both off to make them an ordinary employee.
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <AlertDialog
        title={`Are you sure you want to promote ${dashboardReducer?.employeeInfo?.first_name} ${dashboardReducer?.employeeInfo?.last_name} to an admin ?`}
        description={"Please do note that you're promoting this hr to an admin"}
        setOpen={(value) => {
          setShowHrToAdminConfirm({ "hr": null, show: value })
        }} open={showHrToAdminConfirm.show} handleAccept={() => {
          if (showHrToAdminConfirm.hr) {
            promoteHrToAdminActionMethod({
              hr: showHrToAdminConfirm.hr
            });
            setShowHrToAdminConfirm({ "hr": null, show: false })
          }
        }} />

      <AlertDialog
        open={!!pendingPermission}
        setOpen={(value) => {
          // Cancel resets the pending state — the actual switches will snap
          // back to the reducer's is_admin/is_hr on next render.
          if (!value) setPendingPermission(null);
        }}
        title={
          pendingPermission
            ? `Set ${dashboardReducer?.employeeInfo?.first_name || "this employee"}'s role to ${permissionLabel(pendingPermission)}?`
            : ""
        }
        description={
          pendingPermission
            ? (pendingPermission.is_admin
                ? "Admins can manage employees, approve join requests, and change permissions."
                : pendingPermission.is_hr
                ? "HRs can manage day-to-day employee records."
                : "Ordinary employees have no elevated permissions.")
            : ""
        }
        handleAccept={confirmPermissionChange}
      />
      <Modal
        open={openSuccessDialog && dashboardReducer?.promoteHRToAdmin?.hr === dashboardReducer?.employeeInfo?.id}
        onClose={() => {
          setOpenSuccessDialog(false);

        }}
      >
        <div style={modalStyle} className={classes.modalPaper}>
          <div className={classes.container}>
            <div className={classes.bingoImage}>
              <img src={"/images/success_bingo.svg"} alt="success_bingo" />
            </div>
            <div className={classes.subText} style={{ textAlign: "center" }}>
              Promotion to Admin !
              <br /> You've successfully upgraded user {dashboardReducer?.employeeInfo?.first_name} {dashboardReducer?.employeeInfo?.last_name}
              <br /> From HR to an admin user.
            </div>
            <div style={{ marginTop: "70px" }}>
              <CommonButton
                type="button"
                text={"Done"}
                stylevariant="secondary1"
                onClick={() => {
                  setOpenSuccessDialog(false);
                }}
              />
            </div>
          </div>
        </div>
      </Modal>
      {canViewTransactions && <TransactionHistory employeeDetail={employeeDetail} />}
      <ConfirmSwitchModal
        openModal={confirmSwitch}
        setConfirmation={(confirmation) => {
          setConfirmSwitch((temp) => !temp);
          setConfirmation(confirmation);
        }}
      />
      {canDownloadStatement && (
        <DownloadStatementModal
          open={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          employeeId={dashboardReducer?.employeeInfo?.id}
          employeeName={`${dashboardReducer?.employeeInfo?.first_name || ""} ${dashboardReducer?.employeeInfo?.last_name || ""}`.trim()}
        />
      )}
    </div>
  ) : null;
}

export default EmployeeDetail;

function getPriorityDataForSignedInUser(data1, data2) {
  const user = JSON.parse(localStorage.getItem("user"));

  if (user?.is_super) {
    return data1;
  } else {
    return data2;
  }
}